<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import campaignReportsAPI from 'dashboard/api/campaignReports';

// As conversas que formam UM numero da linha da campanha.
//
// E a diferenca entre um relatorio e um palpite: o usuario clica em "1.188
// enviadas" e ve as 1.188 pessoas, com telefone, status e link para a conversa.
// Paginado no servidor porque uma campanha grande tem dezenas de milhares.

const props = defineProps({
  campaign: { type: Object, required: true },
  status: { type: String, default: null },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

// Primeiro render acontece antes do onMounted: com isLoading=false e data=null
// o v-else caia em data.recipients e estourava TypeError ao abrir o diálogo.
const isLoading = ref(true);
const hasError = ref(false);
const page = ref(1);
const data = ref(null);

// O status vinha do backend em ingles cru (read/delivered/sent) numa tela em
// portugues. Chave desconhecida cai no proprio valor, nunca em vazio.
const recipientStatusLabel = status => {
  if (!status) return '\u00b7';
  const key = `REPORT.CAMPAIGNS.RECIPIENT_STATUS.${String(status).toUpperCase()}`;
  const label = t(key);
  return label === key ? status : label;
};

const load = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await campaignReportsAPI.recipients(props.campaign.id, {
      status: props.status,
      page: page.value,
    });
    data.value = response.data;
  } catch (error) {
    hasError.value = true;
    data.value = null;
  } finally {
    isLoading.value = false;
  }
};

const goTo = next => {
  page.value = next;
  load();
};

onMounted(load);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
    data-testid="campaign-recipients-dialog"
  >
    <div
      class="flex flex-col w-full max-w-2xl max-h-[80vh] overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
    >
      <header class="flex items-center gap-3 px-4 py-3 border-b border-n-weak">
        <div class="flex-1">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ campaign.title }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{
              t(`REPORT.CAMPAIGNS.DRILLDOWN.${(status || 'all').toUpperCase()}`)
            }}
          </p>
        </div>
        <button
          type="button"
          class="p-1 rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          data-testid="campaign-recipients-close"
          @click="emit('close')"
        >
          <span class="i-ri-close-line size-4" />
        </button>
      </header>

      <div
        v-if="isLoading"
        class="px-4 py-8 text-sm text-center text-n-slate-11"
      >
        {{ t('REPORT.CAMPAIGNS.LOADING') }}
      </div>
      <div
        v-else-if="hasError"
        class="flex flex-col items-center gap-3 px-4 py-8"
        data-testid="campaign-recipients-error"
      >
        <span class="text-sm text-n-slate-12">
          {{ t('REPORT.CAMPAIGNS.ERROR') }}
        </span>
        <button
          type="button"
          class="px-3 py-2 text-sm border rounded-xl border-n-strong text-n-slate-12"
          @click="load"
        >
          {{ t('REPORT.CAMPAIGNS.RETRY') }}
        </button>
      </div>
      <div v-else class="flex-1 overflow-y-auto">
        <table class="w-full text-sm">
          <tbody>
            <tr
              v-for="recipient in data?.recipients || []"
              :key="recipient.id"
              class="border-b border-n-weak"
            >
              <td class="px-4 py-2 text-n-slate-12">
                {{ recipient.contact_name || recipient.phone_number }}
              </td>
              <td class="px-4 py-2 text-n-slate-11">
                {{ recipient.phone_number }}
              </td>
              <td class="px-4 py-2 text-xs text-n-slate-11">
                {{ recipientStatusLabel(recipient.status) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer
        v-if="data && data.total_pages > 1"
        class="flex items-center justify-between px-4 py-3 text-xs border-t border-n-weak text-n-slate-11"
      >
        <span>
          {{
            t('REPORT.CAMPAIGNS.DRILLDOWN.PAGE', {
              page: data.page,
              pages: data.total_pages,
              total: data.total_count,
            })
          }}
        </span>
        <span class="flex gap-2">
          <button
            type="button"
            :disabled="data.page <= 1"
            class="px-2 py-1 border rounded-lg border-n-strong disabled:opacity-40"
            @click="goTo(data.page - 1)"
          >
            <span class="i-ri-arrow-left-s-line size-4" />
          </button>
          <button
            type="button"
            :disabled="data.page >= data.total_pages"
            class="px-2 py-1 border rounded-lg border-n-strong disabled:opacity-40"
            @click="goTo(data.page + 1)"
          >
            <span class="i-ri-arrow-right-s-line size-4" />
          </button>
        </span>
      </footer>
    </div>
  </div>
</template>
